import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Library, Search, Filter, Edit3, Copy, Trash2, ExternalLink, Eye,
    EyeOff, ArchiveRestore, Archive, RefreshCcw, Star, Loader2, Plus,
    BookOpen, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
    Lock,
} from "lucide-react";
import { invalidateArticleCache } from "@/lib/useAllArticles";
import { ARTICLES as STATIC_ARTICLES, ARTICLE_GROUPS } from "@/lib/seoArticles";

/* Admin Article Library — manage every studio-published article AND
 * surface every static SEO article alongside them, all grouped by
 * category with a single search bar.
 *
 * Studio articles (status=published / draft / unpublished / archived)
 * support the full lifecycle: edit · pin · duplicate · unpublish ·
 * archive · delete. Static SEO articles are code-defined in
 * /lib/seoArticles*.js, so they're listed as read-only with a "Static"
 * badge — admins can preview them and copy them into a fresh Studio
 * draft via Duplicate, but can't edit the source from the UI.
 *
 * Soft actions (status changes, duplicate, edit) are immediate. Delete
 * requires an explicit confirmation modal AND a `x-confirm-delete: yes`
 * header on the request.
 *
 * Edits open the existing Content Studio in "edit mode" by routing to
 *   /admin?view=content&edit_id={id}
 * ContentStudio reads that query param and pre-loads the article.
 */

const STATUS_TABS = [
    { v: "all", label: "All" },
    { v: "published", label: "Published" },
    { v: "draft", label: "Drafts" },
    { v: "unpublished", label: "Unpublished" },
    { v: "archived", label: "Archived" },
    { v: "static", label: "Static (code)" },
];

const STAGES = [
    { v: "", label: "All stages" },
    { v: "early", label: "Early" },
    { v: "middle", label: "Middle" },
    { v: "late", label: "Late" },
    { v: "all", label: "All-stages tag" },
];

function categoryFor(row) {
    if (row.category) return row.category;
    return ARTICLE_GROUPS[row.group]?.label || row.group || "Uncategorized";
}

// Normalize a static article (from seoArticles.js) into the same row
// shape as a dynamic Studio article so the table can iterate one list
// without forking the renderer. Static rows are read-only — admin
// actions like edit/delete/pin are disabled in the UI for these.
function adaptStatic(a) {
    return {
        id: `static:${a.group}/${a.slug}`,
        slug: a.slug,
        group: a.group,
        title: a.title,
        h1: a.h1 || a.title,
        meta: a.meta,
        summary: a.summary,
        category: a.category || ARTICLE_GROUPS[a.group]?.label || a.group,
        stage: a.stage || "all",
        status: "published",
        view_count: 0,
        featured_order: a.featured_order ?? null,
        updated_at: a.updated_at || null,
        source: "static",
        tags: a.tags || [],
    };
}

function statusPill(status) {
    const s = status || "published";
    const cls = ({
        published: "bg-stage_early/25 text-stage_early-dark",
        draft: "bg-cream-dark text-[#4C5551]",
        unpublished: "bg-coral-light/40 text-coral-dark",
        archived: "bg-yellow-soft text-yellow-dark",
    })[s] || "bg-cream-light text-[#4C5551]";
    return (
        <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-0.5 ${cls}`}>
            {s}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
}

export default function ArticleLibrary() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [stageFilter, setStageFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [search, setSearch] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null); // article object
    const [actionId, setActionId] = useState(null);
    const [editing, setEditing] = useState(null); // article id being inline-edited
    // Collapsed-by-default category panels — persisted so the founder
    // doesn't have to re-collapse them every time they re-open the view.
    const [collapsed, setCollapsed] = useState(() => {
        if (typeof window === "undefined") return {};
        try {
            const raw = window.localStorage.getItem("ml_admin_lib_collapsed");
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        try { window.localStorage.setItem("ml_admin_lib_collapsed", JSON.stringify(collapsed)); }
        catch { /* private mode */ }
    }, [collapsed]);

    // Static articles are local-only — pre-adapt them once.
    const staticRows = useMemo(() => STATIC_ARTICLES.map(adaptStatic), []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            // The "static" pseudo-tab doesn't hit the backend — those rows
            // are code-defined. For every other tab we fetch dynamic
            // articles + merge in static rows on the client.
            if (statusFilter === "static") {
                setRows([]);
                setCounts((prev) => ({ ...prev, static: staticRows.length }));
                setLoading(false);
                return;
            }
            const params = new URLSearchParams();
            if (statusFilter !== "all") params.set("status", statusFilter);
            if (stageFilter) params.set("stage", stageFilter);
            if (categoryFilter) params.set("category", categoryFilter);
            if (search) params.set("q", search);
            const r = await api.get(`/admin/content-studio/articles?${params.toString()}`);
            setRows(r.data.articles || []);
            setCounts({ ...(r.data.counts || {}), static: staticRows.length });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load library.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, stageFilter, categoryFilter, search, staticRows]);

    useEffect(() => { refresh(); }, [refresh]);

    // Build the final visible row list by merging Studio rows with the
    // adapted static rows when the active tab admits them ("all",
    // "published", or the dedicated "static" tab).
    const visibleRows = useMemo(() => {
        const includeStatic = statusFilter === "static"
            || statusFilter === "all"
            || statusFilter === "published";

        // Apply the same client-side filters to static rows that the
        // backend applies to Studio rows. Search matches title, h1,
        // summary, slug, meta, and tags so the UX is identical.
        const q = (search || "").trim().toLowerCase();
        const filteredStatic = !includeStatic ? [] : staticRows.filter((r) => {
            if (stageFilter && (r.stage || "all") !== stageFilter) return false;
            if (categoryFilter && categoryFor(r) !== categoryFilter) return false;
            if (q) {
                const hay = `${r.h1 || ""} ${r.title || ""} ${r.summary || ""} ${r.meta || ""} ${r.slug || ""} ${(r.tags || []).join(" ")}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });

        const dynamic = statusFilter === "static" ? [] : rows;
        return [...dynamic, ...filteredStatic];
    }, [rows, staticRows, statusFilter, stageFilter, categoryFilter, search]);

    // Distinct categories present across both dynamic + static rows.
    const categories = useMemo(() => {
        const set = new Set();
        for (const r of [...rows, ...staticRows]) {
            const c = categoryFor(r);
            if (c) set.add(c);
        }
        return Array.from(set).sort();
    }, [rows, staticRows]);

    // Group rows by category for the collapsible layout. Always sorted
    // alphabetically by category so the order doesn't jitter on edits.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of visibleRows) {
            const cat = categoryFor(r) || "Uncategorized";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(r);
        }
        // Stable secondary sort: newest updated first within each group.
        for (const [, items] of map) {
            items.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [visibleRows]);

    const isCollapsed = (cat) => {
        // Force-expand when any filter/search is active so matches
        // aren't hidden behind a collapsed header.
        if (search || stageFilter || categoryFilter) return false;
        return !!collapsed[cat];
    };
    const toggleCategory = (cat) => {
        setCollapsed((m) => ({ ...m, [cat]: !isCollapsed(cat) }));
    };
    const expandAll = () => {
        const m = {};
        for (const c of categories) m[c] = false;
        setCollapsed(m);
    };
    const collapseAll = () => {
        const m = {};
        for (const c of categories) m[c] = true;
        setCollapsed(m);
    };

    const setStatus = async (article, status) => {
        setActionId(article.id);
        try {
            await api.post(`/admin/content-studio/articles/${article.id}/status`, { status });
            invalidateArticleCache();
            toast.success(`${article.h1 || article.title} → ${status}.`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed.");
        } finally { setActionId(null); }
    };

    const duplicate = async (article) => {
        setActionId(article.id);
        try {
            const r = await api.post(`/admin/content-studio/articles/${article.id}/duplicate`);
            invalidateArticleCache();
            toast.success(`Duplicated to draft: ${r.data.article.title}`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't duplicate.");
        } finally { setActionId(null); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setActionId(confirmDelete.id);
        try {
            await api.delete(`/admin/content-studio/articles/${confirmDelete.id}`, {
                headers: { "x-confirm-delete": "yes" },
            });
            invalidateArticleCache();
            toast.success("Article permanently deleted.");
            setConfirmDelete(null);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally { setActionId(null); }
    };

    const editArticle = (article) => {
        // Deep-link the Content Studio view with the edit id. ContentStudio
        // reads `edit_id` from the URL and loads the article into the editor.
        const p = new URLSearchParams(window.location.search);
        p.set("view", "content");
        p.set("edit_id", article.id);
        window.history.pushState({}, "", `${window.location.pathname}?${p.toString()}`);
        // Manually trigger a popstate so AdminDashboard's view-switcher reads
        // the new ?view=content.
        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    const setFeaturedOrder = async (article) => {
        const current = article.featured_order || "";
        const next = window.prompt(
            `Featured pin order for "${article.h1 || article.title}"\n\n` +
            `Enter a positive integer (1 = highest pin), or leave blank to unpin:`,
            current,
        );
        if (next === null) return;
        const value = next.trim() === "" ? null : Number(next);
        if (value !== null && (!Number.isFinite(value) || value < 1)) {
            toast.error("Featured order must be a positive integer.");
            return;
        }
        setActionId(article.id);
        try {
            await api.patch(`/admin/content-studio/articles/${article.id}`, { featured_order: value });
            invalidateArticleCache();
            toast.success(value === null ? "Unpinned." : `Pinned at position ${value}.`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update pin.");
        } finally { setActionId(null); }
    };

    return (
        <section data-testid="admin-article-library">
            {/* Header with description + expand/collapse controls */}
            <header className="flex items-center gap-3 mb-5">
                <span className="w-11 h-11 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center">
                    <Library size={18} strokeWidth={1.8} />
                </span>
                <div className="flex-1">
                    <h2 className="font-heading text-2xl text-[#12776F]">Article Library</h2>
                    <p className="text-sm text-[#4C5551]">
                        Every studio-published article + every static SEO article in one searchable, categorized view.
                    </p>
                </div>
            </header>

            {/* Status tabs with counts */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="library-status-tabs">
                {STATUS_TABS.map((s) => (
                    <button
                        key={s.v}
                        onClick={() => setStatusFilter(s.v)}
                        data-testid={`library-status-${s.v}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-medium border transition-colors ${statusFilter === s.v
                            ? "bg-teal text-white border-teal"
                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                            }`}
                    >
                        {s.label}
                        <span className="text-[10px] opacity-80">({counts[s.v] ?? 0})</span>
                    </button>
                ))}
            </div>

            {/* Filter bar */}
            <div className="ml-card p-3 mb-4 flex flex-col sm:flex-row gap-2" data-testid="library-filter-bar">
                <label className="flex-1 relative">
                    <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7A74]" />
                    <input
                        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search title, meta, slug, tags…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid="library-search-input"
                    />
                </label>
                <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                    data-testid="library-stage-filter">
                    {STAGES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                    data-testid="library-category-filter">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={refresh} className="ml-btn-secondary !text-xs !px-3 !py-1.5 !min-h-[36px]" data-testid="library-refresh">
                    <RefreshCcw size={12} strokeWidth={2} /> Refresh
                </button>
            </div>

            {/* Expand / collapse all */}
            <div className="flex items-center justify-between mb-3 text-xs" data-testid="library-group-toolbar">
                <span className="text-[#4C5551]">
                    {visibleRows.length} article{visibleRows.length === 1 ? "" : "s"} · {grouped.length} categor{grouped.length === 1 ? "y" : "ies"}
                </span>
                <div className="flex items-center gap-3">
                    <button type="button" onClick={expandAll} data-testid="library-expand-all" className="font-medium text-teal-dark hover:underline">Expand all</button>
                    <button type="button" onClick={collapseAll} data-testid="library-collapse-all" className="font-medium text-[#4C5551] hover:underline">Collapse all</button>
                </div>
            </div>

            {/* Grouped table */}
            <div className="space-y-3" data-testid="library-grouped">
                {loading ? (
                    <div className="ml-card p-8 text-center text-sm text-[#4C5551] flex items-center justify-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Loading library…
                    </div>
                ) : visibleRows.length === 0 ? (
                    <div className="ml-card p-10 text-center" data-testid="library-empty">
                        <BookOpen size={28} strokeWidth={1.4} className="mx-auto text-[#6E7A74] mb-2" />
                        <p className="font-heading text-base text-[#1F2624]">No articles match these filters.</p>
                        <p className="text-xs text-[#4C5551] mt-1">Publish a draft via Content Studio to get started.</p>
                    </div>
                ) : (
                    grouped.map(([cat, items]) => {
                        const catSlug = cat.toLowerCase().replace(/\s+/g, "-");
                        const open = !isCollapsed(cat);
                        return (
                            <div key={cat} className="ml-card overflow-hidden" data-testid={`library-group-${catSlug}`}>
                                <button
                                    type="button"
                                    onClick={() => toggleCategory(cat)}
                                    aria-expanded={open}
                                    data-testid={`library-group-toggle-${catSlug}`}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-cream-light/60"
                                >
                                    <div className="flex items-center gap-2">
                                        {open
                                            ? <ChevronDown size={15} strokeWidth={2} className="text-teal-dark" />
                                            : <ChevronRight size={15} strokeWidth={2} className="text-[#4C5551]" />}
                                        <span className="font-heading text-base text-[#1F2624]">{cat}</span>
                                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] bg-cream-light rounded-2xl px-2 py-0.5">
                                            {items.length}
                                        </span>
                                    </div>
                                </button>
                                {open && (
                                    <div className="border-t border-cream-dark/40 overflow-x-auto">
                                        <table className="w-full text-sm" data-testid={`library-table-${catSlug}`}>
                                            <thead>
                                                <tr className="text-left text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] border-b border-cream-dark">
                                                    <th className="px-3 py-2">Article</th>
                                                    <th className="px-3 py-2">Status</th>
                                                    <th className="px-3 py-2">Stage</th>
                                                    <th className="px-3 py-2">Views</th>
                                                    <th className="px-3 py-2">Updated</th>
                                                    <th className="px-3 py-2">Pin</th>
                                                    <th className="px-3 py-2 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((a) => {
                                                    const url = `/${a.group}/${a.slug}`;
                                                    const status = a.status || "published";
                                                    const isStatic = a.source === "static";
                                                    const busy = actionId === a.id;
                                                    return (
                                                        <tr key={a.id} className="border-b border-cream-dark/40 hover:bg-cream-light/40" data-testid={`library-row-${a.slug}`}>
                                                            <td className="px-3 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-[#1F2624] line-clamp-1">{a.h1 || a.title}</span>
                                                                    {isStatic && (
                                                                        <span
                                                                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#4C5551] bg-cream-dark rounded-2xl px-1.5 py-0.5"
                                                                            title="Defined in /lib/seoArticles.js — read-only"
                                                                            data-testid={`library-static-badge-${a.slug}`}
                                                                        >
                                                                            <Lock size={9} strokeWidth={2.4} /> Static
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1.5">
                                                                    <a
                                                                        href={url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        data-testid={`library-url-${a.slug}`}
                                                                        className="text-[11px] text-teal-dark/80 hover:text-teal-dark hover:underline font-mono truncate max-w-[320px]"
                                                                        title="Open live article"
                                                                    >
                                                                        {url}
                                                                    </a>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            try {
                                                                                navigator.clipboard.writeText(`${window.location.origin}${url}`);
                                                                                toast.success("Article URL copied.");
                                                                            } catch {
                                                                                toast.error("Couldn't copy — highlight the URL instead.");
                                                                            }
                                                                        }}
                                                                        data-testid={`library-copy-url-${a.slug}`}
                                                                        className="p-1 rounded text-[#6E7A74] hover:text-teal-dark hover:bg-cream-light shrink-0"
                                                                        title="Copy full URL"
                                                                    >
                                                                        <Copy size={11} strokeWidth={2} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-3">{statusPill(status)}</td>
                                                            <td className="px-3 py-3 text-[10px] uppercase tracking-wider text-[#6E7A74]">{a.stage || "all"}</td>
                                                            <td className="px-3 py-3 text-xs text-[#4C5551]">{a.view_count?.toLocaleString() || 0}</td>
                                                            <td className="px-3 py-3 text-xs text-[#4C5551]">{fmtDate(a.updated_at)}</td>
                                                            <td className="px-3 py-3">
                                                                {isStatic ? (
                                                                    <span className="text-[10px] text-[#6E7A74]">—</span>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => setFeaturedOrder(a)}
                                                                        disabled={busy}
                                                                        title={a.featured_order ? `Pinned at ${a.featured_order}` : "Unpinned"}
                                                                        data-testid={`library-pin-${a.slug}`}
                                                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] ${a.featured_order
                                                                            ? "bg-coral-light/50 text-coral-dark font-semibold"
                                                                            : "bg-cream-light text-[#6E7A74] hover:bg-cream-dark"
                                                                            }`}
                                                                    >
                                                                        <Star size={11} strokeWidth={2} /> {a.featured_order || "—"}
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-3">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    {(status === "published" || isStatic) && (
                                                                        <Link to={url} target="_blank" rel="noopener noreferrer"
                                                                            data-testid={`library-view-${a.slug}`}
                                                                            className="p-1.5 rounded-md text-[#4C5551] hover:bg-cream-dark"
                                                                            title="View live">
                                                                            <ExternalLink size={13} strokeWidth={2} />
                                                                        </Link>
                                                                    )}
                                                                    {/* Static articles are code-defined — admin can't edit
                                                                        or change status from the UI. Everything else is
                                                                        gated on isStatic so the table stays consistent. */}
                                                                    {!isStatic && (
                                                                        <>
                                                                            <button onClick={() => editArticle(a)} disabled={busy}
                                                                                data-testid={`library-edit-${a.slug}`}
                                                                                className="p-1.5 rounded-md text-teal-dark hover:bg-teal-soft" title="Edit">
                                                                                <Edit3 size={13} strokeWidth={2} />
                                                                            </button>
                                                                            <button onClick={() => duplicate(a)} disabled={busy}
                                                                                data-testid={`library-duplicate-${a.slug}`}
                                                                                className="p-1.5 rounded-md text-[#4C5551] hover:bg-cream-dark" title="Duplicate">
                                                                                <Copy size={13} strokeWidth={2} />
                                                                            </button>
                                                                            {status === "published" ? (
                                                                                <button onClick={() => setStatus(a, "unpublished")} disabled={busy}
                                                                                    data-testid={`library-unpublish-${a.slug}`}
                                                                                    className="p-1.5 rounded-md text-[#4C5551] hover:bg-cream-dark" title="Unpublish">
                                                                                    <EyeOff size={13} strokeWidth={2} />
                                                                                </button>
                                                                            ) : status !== "archived" && (
                                                                                <button onClick={() => setStatus(a, "published")} disabled={busy}
                                                                                    data-testid={`library-republish-${a.slug}`}
                                                                                    className="p-1.5 rounded-md text-stage_early-dark hover:bg-stage_early/20" title="Republish">
                                                                                    <Eye size={13} strokeWidth={2} />
                                                                                </button>
                                                                            )}
                                                                            {status !== "archived" ? (
                                                                                <button onClick={() => setStatus(a, "archived")} disabled={busy}
                                                                                    data-testid={`library-archive-${a.slug}`}
                                                                                    className="p-1.5 rounded-md text-yellow-dark hover:bg-yellow-soft" title="Archive">
                                                                                    <Archive size={13} strokeWidth={2} />
                                                                                </button>
                                                                            ) : (
                                                                                <button onClick={() => setStatus(a, "draft")} disabled={busy}
                                                                                    data-testid={`library-restore-${a.slug}`}
                                                                                    className="p-1.5 rounded-md text-stage_early-dark hover:bg-stage_early/20" title="Restore to draft">
                                                                                    <ArchiveRestore size={13} strokeWidth={2} />
                                                                                </button>
                                                                            )}
                                                                            <button onClick={() => setConfirmDelete(a)} disabled={busy}
                                                                                data-testid={`library-delete-${a.slug}`}
                                                                                className="p-1.5 rounded-md text-coral-dark hover:bg-coral-light/50" title="Delete permanently">
                                                                                <Trash2 size={13} strokeWidth={2} />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Delete confirmation modal */}
            {confirmDelete && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => setConfirmDelete(null)}
                    data-testid="library-delete-modal"
                >
                    <div
                        className="ml-card p-6 max-w-md w-full bg-white"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3">
                            <span className="w-10 h-10 rounded-2xl bg-coral-light/50 text-coral-dark flex items-center justify-center shrink-0">
                                <AlertTriangle size={18} strokeWidth={2} />
                            </span>
                            <div>
                                <h3 className="font-heading text-lg text-[#1F2624]">Delete this article?</h3>
                                <p className="text-sm text-[#4C5551] mt-1.5">
                                    Are you sure you want to permanently delete <strong>{confirmDelete.h1 || confirmDelete.title}</strong>? This cannot be undone.
                                </p>
                                <p className="text-xs text-[#6E7A74] mt-2">
                                    If you only want to hide it from readers, choose <em>Archive</em> instead — archived articles are kept for records and can be restored.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setConfirmDelete(null)} className="ml-btn-secondary !text-xs" data-testid="library-delete-cancel">
                                Cancel
                            </button>
                            <button
                                onClick={doDelete}
                                disabled={actionId === confirmDelete.id}
                                className="ml-btn-primary !bg-coral-dark hover:!bg-coral-dark/90 !text-xs"
                                data-testid="library-delete-confirm"
                            >
                                {actionId === confirmDelete.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={2} />}
                                Yes, delete permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden helpers — eat lint warnings for icons we conditionally render */}
            <noscript className="hidden">{[Plus, Edit3, CheckCircle2, Filter, editing].map(() => "")}</noscript>
        </section>
    );
}
